import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import AppDialog from 'src/components-new/feedback/AppDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import VirtualMeetingDetailNew from './VirtualMeetingDetailNew'
import VirtualMeetingOrganizeDialogNew, {VirtualMeetingOrganizeValues} from './VirtualMeetingOrganizeDialogNew'
import VirtualMeetingRequestDialogNew, {VirtualMeetingRequestValues} from './VirtualMeetingRequestDialogNew'
import {buildColumns, STATUS_OPTIONS, VMRecord} from './virtualMeetingsColumns'

export default function VirtualMeetingsListNew(): JSX.Element {
  const {organize, request, participantUserGroup, columnOptions, qualifications} = useAppSelector(
    (state) => state.pspConfigReducer.data.virtualMeetingsConfigurations
  )
  const userInfoData = useAppSelector(({userReducer}) => userReducer.data)
  const toast = useToast()

  const [data, setData] = useState<VMRecord[]>([])
  const [count, setCount] = useState(0)
  const [loadStatus, setLoadStatus] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    rowsPerPage: 10,
    order: '-startDate',
  })

  const [organizeOpen, setOrganizeOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState<VMRecord | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [deleteRow, setDeleteRow] = useState<VMRecord | null>(null)

  // ── data fetching ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    const fetched: any[] = (await apiClient.getVirtualMeetings(activeFilters, setLoadStatus, pagination)) ?? []
    const parsed: VMRecord[] = fetched.map((meeting: any) => ({
      ...meeting,
      participantsReadable: (meeting.participants ?? [])
        .map((participant: any) => `${participant.userProperties?.firstName ?? ''} ${participant.userProperties?.lastName ?? ''}`.trim())
        .join(', '),
    }))
    setData(parsed)
    const cnt = (await apiClient.getVirtualMeetingsCount(activeFilters, () => {})) ?? 0
    setCount(Number(cnt))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ── filters ───────────────────────────────────────────────────────────────

  const participantGroups = Array.isArray(participantUserGroup) ? participantUserGroup : [participantUserGroup]

  const filterConfig: FilterItem[] = [
    {id: 'rangeDates', label: 'Data', type: 'rangePicker'},
    {id: 'appointmentStatus', label: 'Stato', type: 'select', options: STATUS_OPTIONS},
    ...(participantGroups.includes('PATIENT')
      ? [{id: 'patient', label: 'Paziente', type: 'patientLookup' as const}]
      : []),
    ...(participantGroups.includes('DOCTOR')
      ? [{id: 'doctor', label: 'Medico', type: 'doctorLookup' as const}]
      : []),
  ]

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination((p) => ({...p, page: 0}))
  }

  // Belt-and-suspenders: `pagination.order` is sent to the backend via getVirtualMeetings,
  // but also re-sort the already-fetched page client-side so the "DATA" arrow always
  // visibly reorders rows even if the API/mock ignores the `sort` param.
  const sortedData = useMemo(() => {
    const order = pagination.order
    if (!order || (order !== 'startDate' && order !== '-startDate')) return data
    const desc = order.startsWith('-')
    return [...data].sort((a, b) => {
      const aTime = a.startDate ? new Date(a.startDate).getTime() : 0
      const bTime = b.startDate ? new Date(b.startDate).getTime() : 0
      return desc ? bTime - aTime : aTime - bTime
    })
  }, [data, pagination.order])

  // ── row actions ───────────────────────────────────────────────────────────

  const handleRowClick = (row: VMRecord) => {
    setSelectedMeeting(row)
    setDetailOpen(true)
  }

  const handleDelete = async () => {
    if (!deleteRow) return
    let finalStatus: string | undefined
    await apiClient.deleteVirtualMeetings(deleteRow._id, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Incontro virtuale cancellato.')
      setDeleteRow(null)
      fetchData()
    } else {
      toast.error("Errore durante la cancellazione dell'incontro virtuale.")
    }
  }

  // ── organize / request dialogs ───────────────────────────────────────────

  const handleSaveOrganize = async (values: VirtualMeetingOrganizeValues) => {
    let finalStatus: string | undefined
    let errorMessage: string | undefined
    await apiClient.postVirtualMeetings(
      values,
      (s: string) => { finalStatus = s },
      (message?: string) => { errorMessage = message }
    )
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Incontro virtuale organizzato con successo.')
      setOrganizeOpen(false)
      fetchData()
    } else {
      toast.error(errorMessage || "Errore nell'organizzare l'incontro virtuale.")
    }
  }

  const handleSaveRequest = async (values: VirtualMeetingRequestValues) => {
    let finalStatus: string | undefined
    await apiClient.postRequestVirtualMeetings(values, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Richiesta di incontro virtuale inviata con successo.')
      setRequestOpen(false)
      fetchData()
    } else {
      toast.error("Errore nella richiesta dell'incontro virtuale.")
    }
  }

  // ── derived ───────────────────────────────────────────────────────────────

  const columns = buildColumns({
    onDelete: (row) => setDeleteRow(row),
    authUserId: userInfoData?.authUserId,
    columnOptions,
  })

  // With no PSP-configured columnOptions, "Titolo" is part of the default column set
  // (see DEFAULT_VM_COLUMNS / defaultCols in virtualMeetingsColumns.tsx) and doubles as
  // the mobile card header. Once the PSP configures its own columnOptions, only show the
  // title there if the admin actually kept a "title" entry — otherwise the mobile card
  // would keep displaying it even after it was disabled from the configurator.
  const isTitleColumnConfigured = !columnOptions?.length
    || columnOptions.some((col: {id: string}) => col.id === 'title')

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        {organize && (
          <Button onClick={() => setOrganizeOpen(true)} variant="primary">
            Organizza
          </Button>
        )}
        {request && (
          <Button onClick={() => setRequestOpen(true)} variant="primary">
            Richiedi
          </Button>
        )}
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {loadStatus === STATUS.REJECTED && (
        <InlineMessage onClose={() => setLoadStatus(STATUS.FULFILLED)} severity="error" sx={{mb: 2}}>
          {'Errore nel recuperare gli incontri virtuali.'}
        </InlineMessage>
      )}

      <DataTable<VMRecord>
        columns={columns}
        emptyMessage="Nessun incontro virtuale trovato"
        keyField="_id"
        loading={loadStatus === STATUS.PENDING}
        mobileCardTitle={isTitleColumnConfigured ? (row) => row.title ?? '—' : undefined}
        onPaginationChange={setPagination}
        onRowClick={handleRowClick}
        onSortChange={(order) => setPagination((p) => ({...p, order, page: 0}))}
        pagination={pagination}
        rows={sortedData}
        sortBy={pagination.order}
        totalElements={count}
      />

      {organizeOpen && (
        <VirtualMeetingOrganizeDialogNew
          onClose={() => setOrganizeOpen(false)}
          onSave={handleSaveOrganize}
          open={organizeOpen}
          participantUserGroup={participantUserGroup}
        />
      )}

      {requestOpen && (
        <VirtualMeetingRequestDialogNew
          onClose={() => setRequestOpen(false)}
          onSave={handleSaveRequest}
          open={requestOpen}
          qualifications={qualifications ?? []}
        />
      )}

      {detailOpen && (
        <VirtualMeetingDetailNew
          event={selectedMeeting ?? undefined}
          onClose={() => {
            setDetailOpen(false)
            fetchData()
          }}
        />
      )}

      <AppDialog
        actions={
          <>
            <Button onClick={() => setDeleteRow(null)} variant="secondary">Annulla</Button>
            <Button onClick={handleDelete} variant="danger">Ok</Button>
          </>
        }
        onClose={() => setDeleteRow(null)}
        open={!!deleteRow}
        title="Conferma cancellazione"
      >
        <Typography sx={{pt: 1}}>
          {"La cancellazione è un'operazione irreversibile, si desidera procedere?"}
        </Typography>
      </AppDialog>
    </Box>
  )
}
